import { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  FileText, 
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Building2,
  User,
  Crown,
  Shield,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { vendorAdminApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFieldName = (field) => {
  return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const TierBadge = ({ tier }) => {
  const tierLower = (tier || 'claimed').toLowerCase();
  
  if (tierLower === 'enterprise') {
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1">
        <Crown className="w-3 h-3" />
        Priority
      </Badge>
    );
  }
  if (tierLower === 'verified') {
    return (
      <Badge className="bg-blue-100 text-blue-700 border-blue-200 gap-1">
        <Shield className="w-3 h-3" />
        Verified
      </Badge>
    );
  }
  return null; // No badge for claimed tier
};

export const VendorEditsReview = () => {
  const [edits, setEdits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedEdit, setExpandedEdit] = useState(null);
  const [reviewingEdit, setReviewingEdit] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const fetchEdits = async () => {
    setLoading(true);
    try {
      const data = await vendorAdminApi.getPendingEdits(filter);
      setEdits(data);
    } catch (err) {
      console.error('Failed to fetch edits:', err);
      toast({ title: 'Error', description: 'Failed to load edits', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEdits();
  }, [filter]);

  const handleReview = async (status) => {
    if (!reviewingEdit) return;
    
    setSubmitting(true);
    try {
      await vendorAdminApi.reviewEdit(reviewingEdit.id, {
        status,
        admin_notes: reviewNotes
      });
      toast({ 
        title: status === 'approved' ? 'Edit approved' : 'Edit rejected',
        description: status === 'approved' 
          ? 'Changes have been applied to the platform profile'
          : 'The vendor has been notified'
      });
      setReviewingEdit(null);
      setReviewNotes('');
      await fetchEdits();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = edits.filter(e => e.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Vendor Edit Reviews</h2>
          <p className="text-sm text-muted-foreground">Review and approve vendor profile changes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={filter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('pending')}
          >
            <Clock className="w-4 h-4 mr-2" />
            Pending
            {pendingCount > 0 && (
              <Badge variant="secondary" className="ml-2">{pendingCount}</Badge>
            )}
          </Button>
          <Button
            variant={filter === 'approved' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('approved')}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Approved
          </Button>
          <Button
            variant={filter === 'rejected' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('rejected')}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Rejected
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchEdits}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : edits.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {filter === 'pending' ? 'No pending edits to review' : `No ${filter} edits`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {edits.map(edit => {
            const isExpanded = expandedEdit === edit.id;
            const changedFields = Object.keys(edit.field_changes || {});
            
            return (
              <Card key={edit.id} className={cn(
                edit.vendor_tier?.toLowerCase() === 'enterprise' && 'border-amber-200 bg-amber-50/30',
                edit.vendor_tier?.toLowerCase() === 'verified' && 'border-blue-200 bg-blue-50/20'
              )}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium">{edit.platform_name}</h3>
                        <TierBadge tier={edit.vendor_tier} />
                        <Badge variant={
                          edit.status === 'pending' ? 'secondary' :
                          edit.status === 'approved' ? 'default' : 'destructive'
                        }>
                          {edit.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {edit.vendor_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {edit.vendor_contact}
                        </span>
                        <span>{formatDate(edit.created_at)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {changedFields.length} field{changedFields.length !== 1 ? 's' : ''} changed: {changedFields.map(formatFieldName).join(', ')}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {edit.status === 'pending' && (
                        <Button size="sm" onClick={() => setReviewingEdit(edit)}>
                          Review
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedEdit(isExpanded ? null : edit.id)}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Changes View */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      {Object.entries(edit.field_changes || {}).map(([field, value]) => (
                        <div key={field} className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{formatFieldName(field)}</Label>
                          <div className="p-3 bg-muted rounded-md text-sm">
                            {typeof value === 'object' ? (
                              <pre className="whitespace-pre-wrap text-xs">
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : (
                              <p>{String(value)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {edit.status !== 'pending' && edit.admin_review_notes && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Admin Notes</Label>
                          <div className="p-3 bg-muted rounded-md text-sm">
                            <p>{edit.admin_review_notes}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Reviewed by {edit.reviewed_by} on {formatDate(edit.reviewed_at)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewingEdit} onOpenChange={() => setReviewingEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Edit Request</DialogTitle>
            <DialogDescription>
              Review changes submitted by {reviewingEdit?.vendor_name} for {reviewingEdit?.platform_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="max-h-64 overflow-y-auto space-y-3">
              {reviewingEdit && Object.entries(reviewingEdit.field_changes || {}).map(([field, value]) => (
                <div key={field} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{formatFieldName(field)}</Label>
                  <div className="p-2 bg-muted rounded text-sm">
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="space-y-2">
              <Label>Review Notes (optional)</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add notes about this review..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setReviewingEdit(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleReview('rejected')}
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reject
            </Button>
            <Button 
              onClick={() => handleReview('approved')}
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
